import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Button,
  TextField,
  Checkbox,
  CircularProgress,
  Tab,
  Tabs,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
} from '@mui/material';
import {
  SearchOutlined,
  DeleteOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import type {
  RunStageNodeRef,
  MaterialDestiny,
  MaterialSearchResult,
  LabCollection,
  ProcessedPlanNode,
} from '@/types/run-stage.types';
import {
  searchMaterials,
  getUserLabs,
  updatePlanDestiny,
  deletePlanBooking,
  updatePlan,
  replaceExecuteName,
  getRunPlan,
  getRunExecute,
  getPreviewPlan,
} from '@services/runApi.service';
import { useRunStageStore } from '@/run-stage/store/runStageStore';
import { PreviewPlanSteps } from './PreviewPlanSteps';

interface MapMaterialsProps {
  nodes: RunStageNodeRef[];
  runId: string;
  runUid?: string;
  isReadOnly?: boolean;
}

// ── Helpers ──

function formatDestiny(destiny?: MaterialDestiny | string): string {
  if (!destiny) return '—';
  if (typeof destiny === 'string') return destiny;
  switch (destiny.action) {
    case 'discard':
      return 'Discard';
    case 'return':
      return 'Return to Current Storage Location';
    case 'relocate': {
      const location = destiny.toLocation?.name ?? '—';
      const sub = destiny.subPosition ? ` · ${destiny.subPosition}` : '';
      return `Relocate to ${location}${sub}`;
    }
    default:
      return '—';
  }
}

function formatQuantity(node: RunStageNodeRef): string {
  const q = node.material?.quantity;
  if (!q) return '—';
  const amount = q.amount ?? '';
  const units = q.units ?? '';
  return amount || units ? `${amount} ${units}`.trim() : '—';
}

function getBookingStatus(node: RunStageNodeRef): 'booked' | 'pending' {
  const status = node.material?.status;
  if (status === true || status === 'true') return 'booked';
  return 'pending';
}

function processNodes(nodes: RunStageNodeRef[]): ProcessedPlanNode[] {
  const sorted = [...nodes].sort(
    (a, b) => (a.stepData?.orderString ?? '').localeCompare(b.stepData?.orderString ?? '')
  );
  // Group by nodeId for rowspan
  const groupCounts = new Map<string, number>();
  for (const n of sorted) {
    const gid = n.nodeId;
    groupCounts.set(gid, (groupCounts.get(gid) ?? 0) + 1);
  }
  const seenGroups = new Set<string>();
  return sorted.map((n) => {
    const gid = n.nodeId;
    const isFirst = !seenGroups.has(gid);
    if (isFirst) seenGroups.add(gid);
    return {
      ...n,
      nodeGroupId: gid,
      rowspan: isFirst ? groupCounts.get(gid) : 0,
    } as ProcessedPlanNode;
  });
}

// ── Destiny Dialog ──

interface DestinyDialogProps {
  open: boolean;
  current?: MaterialDestiny | string;
  onClose: () => void;
  onSave: (destiny: MaterialDestiny) => void;
}

const DestinyDialog: React.FC<DestinyDialogProps> = ({ open, current, onClose, onSave }) => {
  const currentAction = typeof current === 'string' ? current : current?.action ?? 'discard';
  const [action, setAction] = useState<string>(currentAction);
  const [locationName, setLocationName] = useState(
    typeof current === 'object' ? current?.toLocation?.name ?? '' : ''
  );
  const [subPosition, setSubPosition] = useState(
    typeof current === 'object' ? current?.subPosition ?? '' : ''
  );

  useEffect(() => {
    if (open) {
      const a = typeof current === 'string' ? current : current?.action ?? 'discard';
      setAction(a);
      setLocationName(typeof current === 'object' ? current?.toLocation?.name ?? '' : '');
      setSubPosition(typeof current === 'object' ? current?.subPosition ?? '' : '');
    }
  }, [open, current]);

  const handleSave = () => {
    const destiny: MaterialDestiny = { action };
    if (action === 'relocate') {
      if (locationName) destiny.toLocation = { name: locationName };
      if (subPosition) destiny.subPosition = subPosition;
    }
    onSave(destiny);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Select Destiny</DialogTitle>
      <DialogContent>
        <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
          <FormControlLabel value="discard" control={<Radio />} label="Discard" />
          <FormControlLabel value="return" control={<Radio />} label="Return to Current Storage Location" />
          <FormControlLabel value="relocate" control={<Radio />} label="Relocate" />
        </RadioGroup>
        {action === 'relocate' && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Storage Location Name"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Sub Storage Position"
              value={subPosition}
              onChange={(e) => setSubPosition(e.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ──

export const MapMaterials: React.FC<MapMaterialsProps> = ({
  nodes,
  runId,
  runUid,
  isReadOnly = false,
}) => {
  const refresh = useRunStageStore((s) => s.refresh);
  const uiPermissions = useRunStageStore((s) => s.uiPermissions);

  // Table state
  const processedNodes = useMemo(() => processNodes(nodes), [nodes]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());

  // Sidebar state
  const [sidebarTab, setSidebarTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MaterialSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialSearchResult | null>(null);
  const [labs, setLabs] = useState<LabCollection[]>([]);
  const [selectedLab, setSelectedLab] = useState('');

  // Destiny dialog
  const [destinyDialogOpen, setDestinyDialogOpen] = useState(false);
  const [destinyNode, setDestinyNode] = useState<ProcessedPlanNode | null>(null);

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Load labs on mount
  useEffect(() => {
    getUserLabs()
      .then(setLabs)
      .catch(() => {});
  }, []);

  // Search materials
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const results = await searchMaterials(searchQuery, selectedLab || undefined);
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, selectedLab]);

  // Book material to selected nodes
  const handleBookMaterial = useCallback(async () => {
    if (!selectedMaterial || selectedNodes.size === 0 || !runId) return;
    setActionLoading(true);
    setActionError(null);
    try {
      // Build updated nodes with booked material
      const bookedNodes = processedNodes
        .filter((n) => selectedNodes.has(`${n.nodeId}-${n.cloneNumber ?? 0}`))
        .map((n) => ({
          ...n,
          material: {
            ...n.material,
            materialId: selectedMaterial._id,
            name: selectedMaterial.name,
            status: 'true' as const,
          },
        }));

      await updatePlan({
        runId,
        runUid,
        nodes: bookedNodes,
      });

      await replaceExecuteName({
        runId,
        runUid,
        bookedMaterials: bookedNodes.map((n) => ({
          nodeId: n.nodeId,
          cloneNumber: n.cloneNumber,
          materialId: selectedMaterial._id,
          materialName: selectedMaterial.name,
        })),
      });

      setSelectedNodes(new Set());
      setSelectedMaterial(null);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to book material');
    } finally {
      setActionLoading(false);
    }
  }, [selectedMaterial, selectedNodes, processedNodes, runId, runUid, refresh]);

  // Delete booking
  const handleDeleteBooking = useCallback(
    async (node: ProcessedPlanNode) => {
      if (!runId) return;
      setActionLoading(true);
      setActionError(null);
      try {
        await deletePlanBooking({
          nodeId: node.nodeId,
          cloneNumber: node.cloneNumber ?? 0,
          runId,
          runUid,
        });
        await refresh();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to delete booking');
      } finally {
        setActionLoading(false);
      }
    },
    [runId, runUid, refresh]
  );

  // Destiny
  const handleOpenDestiny = (node: ProcessedPlanNode) => {
    setDestinyNode(node);
    setDestinyDialogOpen(true);
  };

  const handleSaveDestiny = useCallback(
    async (destiny: MaterialDestiny) => {
      if (!destinyNode || !runId) return;
      setDestinyDialogOpen(false);
      setActionLoading(true);
      setActionError(null);
      try {
        await updatePlanDestiny({
          runId,
          runUid,
          node: destinyNode.nodeId,
          cloneNumber: destinyNode.cloneNumber ?? 0,
          destiny,
        });
        await refresh();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to update destiny');
      } finally {
        setActionLoading(false);
      }
    },
    [destinyNode, runId, runUid, refresh]
  );

  // Checkbox toggle
  const toggleNodeSelection = (key: string) => {
    setSelectedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageNodes = processedNodes.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    const allSelected = pageNodes.every((n) =>
      selectedNodes.has(`${n.nodeId}-${n.cloneNumber ?? 0}`)
    );
    if (allSelected) {
      setSelectedNodes(new Set());
    } else {
      setSelectedNodes(new Set(pageNodes.map((n) => `${n.nodeId}-${n.cloneNumber ?? 0}`)));
    }
  };

  const paginatedNodes = processedNodes.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (!nodes.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">No material nodes found for this run.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left column — Material Table */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Action error */}
        {actionError && (
          <Alert severity="error" sx={{ m: 1 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto', m: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {!isReadOnly && (
                  <TableCell padding="checkbox" sx={{ fontWeight: 600 }}>
                    <Checkbox
                      size="small"
                      checked={
                        paginatedNodes.length > 0 &&
                        paginatedNodes.every((n) =>
                          selectedNodes.has(`${n.nodeId}-${n.cloneNumber ?? 0}`)
                        )
                      }
                      indeterminate={
                        paginatedNodes.some((n) =>
                          selectedNodes.has(`${n.nodeId}-${n.cloneNumber ?? 0}`)
                        ) &&
                        !paginatedNodes.every((n) =>
                          selectedNodes.has(`${n.nodeId}-${n.cloneNumber ?? 0}`)
                        )
                      }
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>Template Material / Container</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Step No. / Operator</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mapped Material / Container</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Destiny</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedNodes.map((node, idx) => {
                const key = `${node.nodeId}-${node.cloneNumber ?? 0}`;
                const booking = getBookingStatus(node);
                const destinyLabel = formatDestiny(node.material?.destiny);
                const stepLabel = [
                  node.stepData?.orderString ? `${node.stepData.orderString}.` : '',
                  node.stepData?.name ?? '',
                  node.stepData?.cycleOrder ? `(x${node.stepData.cycleOrder})` : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <TableRow key={`${key}-${idx}`} hover selected={selectedNodes.has(key)}>
                    {!isReadOnly && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedNodes.has(key)}
                          onChange={() => toggleNodeSelection(key)}
                          disabled={node.completed}
                        />
                      </TableCell>
                    )}
                    {(node.rowspan ?? 1) > 0 && (
                      <TableCell rowSpan={node.rowspan || 1}>
                        <Tooltip title={node.nodeName ?? ''} placement="top">
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                            {node.nodeName || node.materialName || node.containerName || node.nodeId}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {node.batchNumber && node.totalBatchSize && node.totalBatchSize > 1 && (
                          <Chip
                            label={`B${node.batchNumber}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              mr: 0.5,
                              backgroundColor: node.batchColor || 'primary.light',
                              color: '#fff',
                            }}
                          />
                        )}
                        {stepLabel || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatQuantity(node)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {node.material?.name || node.materialName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {booking === 'booked' ? (
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                          <Chip label="Booked" color="success" size="small" variant="outlined" />
                          {!isReadOnly && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteBooking(node)}
                              disabled={actionLoading}
                              aria-label="Delete booking"
                            >
                              <DeleteOutlined fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      ) : (
                        <Chip label="Pending" color="default" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {!isReadOnly ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleOpenDestiny(node)}
                          disabled={actionLoading}
                          sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                        >
                          {destinyLabel === '—' ? 'Select' : destinyLabel}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {destinyLabel}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={processedNodes.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ flexShrink: 0 }}
        />
      </Box>

      {/* Right column — Sidebar with tabs */}
      <Box
        sx={{
          width: '30%',
          minWidth: 280,
          maxWidth: 400,
          borderLeft: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={sidebarTab}
          onChange={(_, v) => setSidebarTab(v)}
          variant="fullWidth"
          sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab label="Booking" sx={{ textTransform: 'none', minHeight: 40 }} />
          <Tab label="Preview" sx={{ textTransform: 'none', minHeight: 40 }} />
        </Tabs>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Booking Tab */}
          {sidebarTab === 0 && (
            <Box sx={{ p: 1.5 }}>
              {/* Search input */}
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search materials…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  fullWidth
                  slotProps={{ input: { sx: { fontSize: '0.85rem' } } }}
                />
                <IconButton size="small" onClick={handleSearch} disabled={searchLoading}>
                  {searchLoading ? <CircularProgress size={18} /> : <SearchOutlined />}
                </IconButton>
              </Stack>

              {/* Lab filter */}
              {labs.length > 0 && (
                <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                  <InputLabel sx={{ fontSize: '0.85rem' }}>Lab Filter</InputLabel>
                  <Select
                    value={selectedLab}
                    onChange={(e) => setSelectedLab(e.target.value)}
                    label="Lab Filter"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    <MenuItem value="">All Labs</MenuItem>
                    {labs.map((lab) => (
                      <MenuItem key={lab._id} value={lab._id}>
                        {lab.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Book button */}
              {!isReadOnly && selectedMaterial && selectedNodes.size > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={handleBookMaterial}
                  disabled={actionLoading}
                  sx={{ mb: 1, textTransform: 'none' }}
                >
                  {actionLoading ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  Book "{selectedMaterial.name}" to {selectedNodes.size} node(s)
                </Button>
              )}

              {/* Search results */}
              {searchResults.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {searchResults.length} result(s)
                  </Typography>
                  {searchResults.map((mat) => (
                    <Paper
                      key={mat._id}
                      variant="outlined"
                      sx={{
                        p: 1,
                        mb: 0.5,
                        cursor: 'pointer',
                        backgroundColor:
                          selectedMaterial?._id === mat._id ? 'action.selected' : 'background.paper',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() =>
                        setSelectedMaterial(selectedMaterial?._id === mat._id ? null : mat)
                      }
                    >
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Checkbox
                          size="small"
                          checked={selectedMaterial?._id === mat._id}
                          sx={{ p: 0 }}
                          readOnly
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {mat.name || mat._id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {[
                              mat.uid && `UID: ${mat.uid}`,
                              mat.customId && `ID: ${mat.customId}`,
                              mat.collection,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                          {(mat.location || mat.batchNumber) && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {[
                                mat.location && `Storage Location: ${mat.location}`,
                                mat.batchNumber && `Batch: ${mat.batchNumber}`,
                                mat.quantityAvailable?.amount != null &&
                                  `Qty: ${mat.quantityAvailable.amount} ${mat.quantityAvailable.units ?? ''}`,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              )}

              {searchResults.length === 0 && searchQuery && !searchLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  No materials found
                </Typography>
              )}
            </Box>
          )}

          {/* Preview Tab */}
          {sidebarTab === 1 && (
            <PreviewPlanSteps runId={runId} runUid={runUid} />
          )}
        </Box>
      </Box>

      {/* Destiny dialog */}
      <DestinyDialog
        open={destinyDialogOpen}
        current={destinyNode?.material?.destiny}
        onClose={() => setDestinyDialogOpen(false)}
        onSave={handleSaveDestiny}
      />
    </Box>
  );
};
